import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { toast } from "react-toastify";
import {
  People,
  PersonOff,
  TrendingUp,
  AttachMoney,
  Assessment,
  Warning,
  Star,
  Schedule,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#17134b", "#2d2a72", "#10b981", "#f59e0b", "#ef4444"];

export default function DashboardAnalytics() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get("/analytics/dashboard");
      setAnalytics(response.data);
    } catch (err) {
      console.error("Failed to fetch analytics", err);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card">
              <div className="w-12 h-12 bg-gray-200 rounded-2xl mb-4 animate-pulse"></div>
              <div className="h-8 bg-gray-200 rounded mb-2 animate-pulse w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="card text-center py-12">
          <p className="text-gray-600">No analytics data available</p>
        </div>
      </div>
    );
  }

  const attendanceChartData =
    analytics.monthly_attendance?.map((item) => ({
      date: new Date(item._id).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      present: item.present,
      absent: item.absent,
    })) || [];

  const salaryChartData =
    analytics.salary_expenses?.map((item) => ({
      department: item._id || "Unassigned",
      amount: item.total_salary || 0,
      employees: item.employee_count || 0,
    })) || [];

  const leavesChartData =
    analytics.leaves_by_department?.map((item) => ({
      name: item._id || "Unassigned",
      value: item.total_leaves || 0,
    })) || [];

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold text-primary mb-2">
          HR Analytics Dashboard
        </h1>
        <p className="text-gray-600 text-lg">
          Comprehensive insights into your organization's HR metrics
        </p>
      </motion.div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500 rounded-2xl text-white">
              <People className="w-6 h-6" />
            </div>
            <div className="text-xs font-semibold text-gray-500 uppercase">
              Present Today
            </div>
          </div>
          <div className="text-4xl font-bold text-primary mb-1">
            {analytics.present_absent?.present || 0}
          </div>
          <div className="text-sm text-gray-600">
            Out of {analytics.present_absent?.total || 0} employees
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-500 rounded-2xl text-white">
              <PersonOff className="w-6 h-6" />
            </div>
            <div className="text-xs font-semibold text-gray-500 uppercase">
              Absent Today
            </div>
          </div>
          <div className="text-4xl font-bold text-primary mb-1">
            {analytics.present_absent?.absent || 0}
          </div>
          <div className="text-sm text-gray-600">Employees absent</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-500 rounded-2xl text-white">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="text-xs font-semibold text-gray-500 uppercase">
              Attrition Rate
            </div>
          </div>
          <div className="text-4xl font-bold text-primary mb-1">
            {analytics.attrition_rate?.toFixed(2) || "0.00"}%
          </div>
          <div className="text-sm text-gray-600">This month</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500 rounded-2xl text-white">
              <AttachMoney className="w-6 h-6" />
            </div>
            <div className="text-xs font-semibold text-gray-500 uppercase">
              Total Salary
            </div>
          </div>
          <div className="text-4xl font-bold text-primary mb-1">
            ₹
            {(
              analytics.salary_expenses?.reduce(
                (sum, item) => sum + (item.total_salary || 0),
                0
              ) / 100000
            ).toFixed(1) || "0"}
            L
          </div>
          <div className="text-sm text-gray-600">This month</div>
        </motion.div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Attendance Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className="card"
        >
          <h2 className="text-2xl font-bold text-primary mb-6">
            Monthly Attendance Trend
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={attendanceChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="present"
                stroke="#10b981"
                strokeWidth={2}
                name="Present"
              />
              <Line
                type="monotone"
                dataKey="absent"
                stroke="#ef4444"
                strokeWidth={2}
                name="Absent"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Salary Expenses Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
          className="card"
        >
          <h2 className="text-2xl font-bold text-primary mb-6">
            Salary Expenses by Department
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={salaryChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="department" />
              <YAxis />
              <Tooltip
                formatter={(value) => `₹${(value / 100000).toFixed(1)}L`}
              />
              <Legend />
              <Bar dataKey="amount" fill="#17134b" name="Total Salary" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaves by Department */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7 }}
          className="card"
        >
          <h2 className="text-2xl font-bold text-primary mb-6">
            Leaves Taken by Department
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={leavesChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {leavesChartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Top Latecomers */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
          className="card"
        >
          <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
            <Schedule className="mr-2" />
            Top Latecomers
          </h2>
          <div className="space-y-3">
            {analytics.top_latecomers?.length > 0 ? (
              analytics.top_latecomers.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {item.employee_name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {item.late_count} late arrivals
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg text-sm font-semibold">
                    {item.late_count}x
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">
                No latecomers data available
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Top Performers */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="card"
      >
        <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
          <Star className="mr-2" />
          Top Performers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {analytics.top_performers?.length > 0 ? (
            analytics.top_performers.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold mr-3">
                      {index + 1}
                    </div>
                    <div className="font-semibold text-gray-900">
                      {item.employee_name}
                    </div>
                  </div>
                </div>
                <div className="flex items-center mt-3">
                  <Star className="text-yellow-500 mr-1" />
                  <span className="text-lg font-bold text-primary">
                    {item.avg_score?.toFixed(1) || "N/A"}
                  </span>
                  <span className="text-sm text-gray-600 ml-1">/ 100</span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 col-span-full text-center py-8">
              No performance data available yet
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
